'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import {
  BlogTLDR,
  BlogKeyTakeaways,
  BlogFAQ,
  BlogSources,
  BlogLastUpdated,
  BlogComparisonTable,
  BlogAppCTA,
  BlogWhatTheAppChecks,
} from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Sparkling Water: LaCroix, Bubly & Spindrift Ranked (2026)"
      author="MicroPlastics Research Desk"
      date="July 29, 2026"
      readTime="10 min read"
      heroImage="/blog/heroes/water.webp"
      heroImageAlt="Cans and bottles of sparkling water on a kitchen counter"
    >
      <BlogLastUpdated iso="2026-07-29" formatted="July 29, 2026" />

      <BlogTLDR>
        <strong>Yes, and the container matters more here than it does for still
        water.</strong> No brand publishes microplastic testing on its sparkling
        water, so the honest ranking is by packaging. The mechanism that makes
        carbonated drinks different is <strong>pressure and agitation</strong>:
        dissolved CO<sub>2</sub> keeps a bottle under constant internal pressure,
        and every shake, drop, and temperature swing works that pressure against
        the container wall. Mechanical stress is an established driver of
        particle release from plastic bottles, which means a{' '}
        <strong>PET bottle of sparkling water is a worse choice than a PET
        bottle of still water</strong>. Aluminum cans sidestep the shedding
        problem but introduce a thin internal polymer liner and its bisphenol
        questions. <strong>Glass is the only format with neither issue</strong>,
        and Topo Chico, Perrier and San Pellegrino all sell it.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>No sparkling water brand has published a particle count, so treat any specific number you see as invented. Packaging is the axis you can actually reason about.</>,
          <>Carbonation adds constant internal pressure, and mechanical stress is a documented driver of microplastic release from plastic bottles. Fizzy in PET is worse than still in PET.</>,
          <>Cans avoid bottle shedding but have an epoxy or acrylic inner liner, so they trade a microplastics question for a bisphenol one.</>,
          <>Glass is the only genuinely clean format. Topo Chico, Perrier and San Pellegrino all offer it, usually for a few cents more per litre.</>,
          <>Heat is the multiplier for every plastic format. A case of seltzer stored in a hot garage or car boot is the worst-case scenario.</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_sparkling_water" />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why carbonation changes the answer
      </h2>
      <p>
        Most of what you read about microplastics in bottled water is about{' '}
        <em>still</em> water, and the headline finding there is already
        substantial: a 2024 study using stimulated Raman scattering microscopy
        counted roughly <strong>240,000 detectable plastic particles per
        litre</strong> in common bottled waters, the large majority of them
        nanoplastics small enough that earlier methods missed them entirely.
        That work was done on ordinary PET bottled water.
      </p>
      <p>
        Carbonated water sits on top of that baseline with an extra mechanism.
        Dissolved CO<sub>2</sub> means the container is{' '}
        <strong>permanently pressurised from the inside</strong>. Separate
        research on plastic bottles has shown that{' '}
        <strong>mechanical stress</strong>, squeezing, shaking, repeated
        opening, and temperature cycling, measurably increases particle release
        from the bottle wall. Carbonation supplies that stress continuously
        rather than occasionally, and a warm bottle raises the internal pressure
        further.
      </p>
      <p>
        This is why the usual advice to &ldquo;just buy the big plastic bottle
        and decant&rdquo; works less well for seltzer than for still water. It
        is also why the can-versus-bottle decision is worth more here than
        almost anywhere else in the drinks aisle.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The three formats, honestly
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Glass (best).</strong> No polymer in contact with the water at
          all. The cap liner is a tiny sealing surface and the only plastic in
          the system. This is the only format where the microplastics question
          essentially goes away.
        </li>
        <li>
          <strong>Aluminum can (good, with an asterisk).</strong> Aluminum
          cannot shed microplastics, so the PET problem disappears. But every
          food-grade can has a thin internal <strong>epoxy or acrylic
          liner</strong> to stop the metal reacting with the contents, and that
          liner is where bisphenol questions live. Most major brands have moved
          to BPA-NI (&ldquo;non-intent&rdquo;) liners, which means BPA is not
          deliberately used, not that the liner is inert. See{' '}
          <Link href="/blog/are-cans-lined-with-plastic" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            are cans lined with plastic
          </Link>{' '}
          for the full picture.
        </li>
        <li>
          <strong>PET plastic bottle (worst).</strong> You get the still-water
          baseline plus the carbonation stress on top, plus whatever the bottle
          picked up from warehouse heat and shelf time. Large multi-serve PET
          bottles are the worst case because they are opened and resealed
          repeatedly, each cycle adding another pressure swing.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Sparkling water brands ranked by packaging
      </h2>

      <BlogComparisonTable
        caption="Major sparkling water brands through a plastics lens. Ranked on packaging, not on a particle count, because no brand publishes one."
        columns={[
          { key: 'brand', label: 'Brand' },
          { key: 'pack', label: 'Usual packaging' },
          { key: 'driver', label: 'Main plastic driver' },
          { key: 'verdict', label: 'Verdict' },
        ]}
        rows={[
          { brand: 'Topo Chico', pack: 'Glass bottle (also cans)', driver: 'Cap liner only', verdict: 'Best available. Buy the glass.' },
          { brand: 'Perrier', pack: 'Glass bottle, can, or PET', driver: 'Depends entirely on format', verdict: 'Excellent in glass, ordinary in PET' },
          { brand: 'San Pellegrino', pack: 'Glass bottle, can, or PET', driver: 'Depends entirely on format', verdict: 'Excellent in glass, ordinary in PET' },
          { brand: 'Spindrift', pack: 'Aluminum can', driver: 'Can liner', verdict: 'Strong. Real fruit, no plastic bottle' },
          { brand: 'Waterloo', pack: 'Aluminum can', driver: 'Can liner', verdict: 'Strong. Can-only line-up' },
          { brand: 'LaCroix', pack: 'Aluminum can', driver: 'Can liner', verdict: 'Strong. Can-only line-up' },
          { brand: 'Bubly', pack: 'Aluminum can', driver: 'Can liner', verdict: 'Strong. Can-only line-up' },
          { brand: 'Liquid Death (sparkling)', pack: 'Aluminum can', driver: 'Can liner', verdict: 'Strong, same logic as the still version' },
          { brand: 'Store-brand seltzer', pack: 'Usually PET bottle', driver: 'Bottle shedding + carbonation stress', verdict: 'Weakest common option' },
          { brand: 'Home carbonator (SodaStream etc.)', pack: 'Reusable PET or glass carafe', driver: 'Carafe material, your choice', verdict: 'Best overall if you pick the glass carafe' },
        ]}
      />
      <p>
        The pattern is unmistakable and it has nothing to do with brand quality
        or price: <strong>the flavour house you prefer barely matters, the
        container decides your exposure</strong>. A budget can beats a premium
        PET bottle on this measure every time.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The home-carbonation option
      </h2>
      <p>
        If you drink sparkling water daily, carbonating filtered water at home
        is the single biggest reduction available, because it removes both the
        warehouse heat and the shelf time. Two caveats worth knowing:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Choose the glass carafe.</strong> Most home carbonators ship
          with a reusable PET bottle by default, and a reusable pressurised PET
          bottle that gets washed, refilled and re-pressurised hundreds of times
          is exactly the mechanical-stress scenario the research describes.
          Glass carafe versions are widely available.
        </li>
        <li>
          <strong>Filter first.</strong> Carbonating tap water carbonates
          whatever is already in it. A filter that is actually rated for
          particle reduction does the useful work here, see{' '}
          <Link href="/blog/microplastics-water-filters-compared" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            water filters compared
          </Link>
          .
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical rules that actually move the number
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Glass first, can second, PET last.</strong> That ordering holds across every brand.</li>
        <li><strong>Never store it warm.</strong> Heat raises both plastic shedding and internal pressure. A case in a hot garage, car boot, or sunny porch is the worst thing you can do to it.</li>
        <li><strong>Skip the big resealable PET bottles.</strong> Repeated opening and resealing means repeated pressure cycling on the same wall.</li>
        <li><strong>Don&apos;t freeze or shake PET.</strong> Both are mechanical stress, and a shaken carbonated bottle is under far more load than a still one.</li>
        <li><strong>If you buy cans, that is a genuinely good choice</strong>, just know you have swapped a shedding question for a liner question rather than eliminating both.</li>
      </ul>

      <BlogWhatTheAppChecks
        items={[
          <>The container material of the exact sparkling water you buy, read off the label, including which brands sell the same product in glass.</>,
          <>A 0&ndash;100 risk score that weighs packaging, carbonation, and format together rather than treating all bottled water alike.</>,
          <>A lower-plastic swap in the same flavour category, usually a canned or glass version of something you already drink.</>,
          <>Your running drinks profile, so a daily seltzer habit shows up as a pattern instead of a one-off scan.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan the seltzer you actually buy"
        sub="Glass, can, or PET changes the answer more than the brand on the label does. Scan your usual sparkling water with MicroPlastics for a 0&ndash;100 score and the lowest-plastic version of the same drink."
        buttonLabel="Scan my sparkling water"
      />

      <p>
        Related:{' '}
        <Link href="/blog/microplastics-bottled-water-brands-ranked" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          bottled water brands ranked
        </Link>
        ,{' '}
        <Link href="/blog/are-cans-lined-with-plastic" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          are cans lined with plastic
        </Link>
        ,{' '}
        <Link href="/blog/does-liquid-death-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          does Liquid Death have microplastics
        </Link>
        , and{' '}
        <Link href="/blog/water-bottle-left-in-hot-car-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          water left in a hot car
        </Link>
        .
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Does sparkling water have more microplastics than still water?',
            answer:
              'In the same plastic bottle, very likely yes. Dissolved CO2 keeps the container under constant internal pressure, and mechanical stress is an established driver of particle release from plastic bottles. Carbonation supplies that stress continuously. In a can or a glass bottle the comparison reverses, because neither format can shed PET at all.',
          },
          {
            question: 'Is LaCroix or Bubly better for microplastics?',
            answer:
              'There is no meaningful difference between them. Both are sold almost exclusively in aluminum cans, so both avoid the plastic-bottle shedding problem and both carry the same internal can-liner question. Neither publishes microplastic testing. Choose on flavour, and if you want a genuine upgrade, move to a glass-bottled brand such as Topo Chico.',
          },
          {
            question: 'Are aluminum cans actually free of plastic?',
            answer:
              'No. Every food-grade aluminum can has a thin internal polymer liner, usually epoxy or acrylic, to stop the metal reacting with the contents. It is a very thin layer and it does not shed microplastics the way a PET bottle does, but it is the reason a can is not a zero-plastic container. Most large brands now use BPA-NI liners, which means BPA is not intentionally added.',
          },
          {
            question: 'Does a SodaStream reduce microplastics?',
            answer:
              'It can, substantially, but only if you use the glass carafe and filter the water first. The default reusable PET bottle gets washed, refilled and re-pressurised repeatedly, which is precisely the mechanical-stress cycle that increases particle release. With a glass carafe and filtered water it is the lowest-plastic way to drink sparkling water daily.',
          },
          {
            question: 'Does leaving seltzer in a hot car make it worse?',
            answer:
              'Yes, and more so than for still water. Heat independently increases microplastic release from plastic, and it also raises the internal pressure of a carbonated container, adding mechanical load on top. Cans are far less affected than PET bottles, but no plastic-containing format benefits from being stored warm.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Qian N, Gao X, Lang X, et al.',
            year: 2024,
            title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy',
            publisher: 'Proceedings of the National Academy of Sciences (PNAS)',
            url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121',
          },
          {
            authors: 'Winkler A, Santo N, Ortenzi MA, et al.',
            year: 2022,
            title: 'Does mechanical stress cause microplastic release from plastic water bottles?',
            publisher: 'Water Research',
            url: 'https://www.sciencedirect.com/science/article/pii/S0043135422000453',
          },
          {
            authors: 'Kosuth M, Mason SA, Wattenberg EV',
            year: 2018,
            title: 'Anthropogenic contamination of tap water, beer, and sea salt',
            publisher: 'PLOS ONE',
            url: 'https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0194970',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
